<script>
    import {eventBridge, portalConfiguration} from "../../stores.js";
    import {
        Button,
        Collapse,
        Form,
        FormGroup,
        Input,
        Label,
        Modal,
        ModalBody,
        ModalFooter,
        ModalHeader
    } from "@sveltestrap/sveltestrap";
    import ButtonLine from "../snippets/ButtonLine.svelte";
    import InputPin from "../form/InputPin.svelte";
    import InputFile from "../form/InputFile.svelte";
    import Help from "../snippets/Help.svelte";
    import {onMount} from "svelte";
    import ConfirmModal from "./ConfirmModal.svelte";
    import Fa from "svelte-fa";
    import {faTriangleExclamation} from "@fortawesome/free-solid-svg-icons";
    import {handleStaticModalToggle, modalClassName, modalFade} from "./utils.js";

    let refSubmitButton = $state();
    let { showModal = $bindable(false), submit, ...rest } = $props();
    let showAlreadyExists = $state(false);
    const toggle = () => (showModal = !showModal);
    const handleModalToggle = handleStaticModalToggle(toggle);

    let validPin = $state(true);
    let validPinRepeat = $state(true);

    let optionalNewPin = $state(false);
    let pin = $state('');
    let newPin = $state('');
    let newPinRepeat = $state('');
    let fileName = $state();
    let fileExtension = $state();
    let fileBytes = $state();

    $effect(() => {
        if (!showModal) {
            validPin = true;
            validPinRepeat = true;
            optionalNewPin = false;
            pin = '';
            newPin = '';
            newPinRepeat = '';
            fileName = undefined;
            fileExtension = undefined;
            fileBytes = undefined;
        }
    });

    function handleSubmit() {
        if (optionalNewPin && newPin !== newPinRepeat) {
            validPinRepeat = false;
            return;
        }

        validPin = true;
        validPinRepeat = true;

        $eventBridge.dispatchEvent('cs:addTokenToCache', {
            fileExtension: fileExtension,
            fileBytes: Array.from(fileBytes),
            pin,
            optionalCachePin: optionalNewPin ? newPin : null
        });
        submit?.();
    }

    function handleFileChange(detail) {
        fileName = detail.fileName;
        fileExtension = detail.fileExtension;
        fileBytes = detail.fileBytes;
    }

    function handleInvalidPin() {
        validPin = false;
    }

    function handleAlreadyExists() {
        showAlreadyExists = true;
    }

    function handleSuccess() {
        pin = '';
        showModal = false;
    }

    let requiredFieldsFilled = $derived(Boolean(fileName && pin && (!optionalNewPin || (newPin && newPinRepeat))));

    onMount(() => {
        $eventBridge.addEventListener('cs:ErrorAddTokenInvalidPin', handleInvalidPin);
        $eventBridge.addEventListener('cs:ErrorAddTokenAlreadyExists', handleAlreadyExists);
        $eventBridge.addEventListener('cs:SuccessAddToken', handleSuccess);

        return () => {
            $eventBridge.removeEventListener('cs:ErrorAddTokenInvalidPin', handleInvalidPin);
            $eventBridge.removeEventListener('cs:ErrorAddTokenAlreadyExists', handleAlreadyExists);
            $eventBridge.removeEventListener('cs:SuccessAddToken', handleSuccess);
        };
    });
</script>

<Modal {...rest} isOpen={showModal} toggle={handleModalToggle} fade={$modalFade} modalClassName={$modalClassName} scrollable size="lg">
    <ModalHeader toggle={handleModalToggle}>{fileName ? "PIN Eingabe" : "Zertifikatsauswahl"}</ModalHeader>
    <ModalBody>
        <Form on:submit={(event) => {event.preventDefault(); handleSubmit();}}>
            <InputFile accept=".p12" autoOpen hidden cancel={() => (showModal = false)} change={handleFileChange}/>

            {#if fileName}
                <FormGroup>
                    <Label>Bitte geben Sie die PIN des ausgewählten Zertifikats ein.</Label>
                    <InputPin bind:pin={pin} invalid={!validPin} autofocus={fileName} placeholder='Ihre PIN für das ausgewählte Zertifikat "{fileName}".' data-test-id="addTokenModal-form-input-pin"/>
                </FormGroup>
                <Input type="checkbox" label="PIN neu vergeben (optional)" bind:checked={optionalNewPin} data-test-id="addTokenModal-form-checkbox-optionalNewPin" />
                <Collapse isOpen={optionalNewPin}>
                    <div class="optional-new-pin-fields">
                        <FormGroup>
                            <Label>Neue PIN</Label>
                            <InputPin bind:pin={newPin} placeholder="Neue PIN festlegen" data-test-id="addTokenModal-form-input-newPin" />
                            <Label>Neue PIN Wiederholung</Label>
                            <InputPin bind:pin={newPinRepeat} invalid={!validPinRepeat} formFeedback="Die eingegebenen PINs stimmen nicht überein." placeholder="Wiederholung der neuen PIN" data-test-id="addTokenModal-form-input-newPinRepeat" />
                        </FormGroup>
                    </div>
                </Collapse>
            {:else}
                <p>Bitte wählen Sie die Zertifikatsdatei (*.p12) in dem bereits geöffneten Dialog aus.</p>
            {/if}
            <Button color="primary" type="submit" bind:inner={refSubmitButton} hidden disabled={!requiredFieldsFilled} data-test-id="addTokenModal-form-button-ok">OK</Button>
        </Form>
    </ModalBody>

    <ModalFooter>
        {#if fileName}
            <ButtonLine>
                {#snippet left()}
                    <Help url={`${$portalConfiguration.help ?? ''}/einrichtung-von-bea/bea-client-security/authentifizieren/pin-eingabe-bei-software-zertifikat-bzw-token`} data-test-id="addTokenModal-form-help" />
                {/snippet}
                <Button color="primary" on:click={() => (showModal = false)} data-test-id="addTokenModal-form-button-cancel">Abbrechen</Button>
                <Button color="primary" on:click={() => refSubmitButton.click()} disabled={!requiredFieldsFilled} data-test-id="addTokenModal-form-button-ok">OK</Button>
            </ButtonLine>
        {/if}
    </ModalFooter>
</Modal>

<ConfirmModal bind:showConfirm={showAlreadyExists} confirm={() => (showModal = false)}>
    {#snippet header()}
        <Fa icon={faTriangleExclamation} />Fehler
    {/snippet}

    Dieses Software-Token ist bereits vorhanden.
</ConfirmModal>

<style>
    .optional-new-pin-fields {
        padding-top: 4px;
    }
</style>
